import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Paper, Typography, Button, TextField, Rating, RadioGroup, FormControlLabel, Radio, Alert, CircularProgress, FormHelperText, FormControl } from '@mui/material';
import { getPublicEvaluation, submitPublicEvaluation, getPublicEvaluationById, submitPublicEvaluationById } from '../utils/api';

const PublicEvaluation = () => {
  const { token, id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const data = token ? await getPublicEvaluation(token) : await getPublicEvaluationById(id);
        setEvaluation(data);
      } catch (e) {
        setError(e?.response?.data?.message || 'Falha ao carregar avaliação');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, id]);

  const handleChange = (qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    // Validação simples de obrigatoriedade
    const errs = {};
    const qs = Array.isArray(evaluation.questions) ? evaluation.questions : [];
    qs.forEach((q, idx) => {
      const qid = idx;
      if (q.required) {
        const val = answers[qid];
        if (q.type === 'rating' && (val !== 'na') && (!val || val <= 0)) errs[qid] = 'Selecione uma classificação.';
        if (q.type === 'text' && (!val || String(val).trim().length === 0)) errs[qid] = 'Campo obrigatório.';
        if (q.type === 'yesno' && (val !== 'yes' && val !== 'no')) errs[qid] = 'Selecione Sim ou Não.';
        if (q.type === 'choice' && (!val || String(val).trim().length === 0)) errs[qid] = 'Selecione uma opção.';
      }
    });
    setValidationErrors(errs);
    if (Object.keys(errs).length) {
      return;
    }
    try {
      const payload = answers || {};
      if (token) {
        await submitPublicEvaluation(token, payload);
      } else {
        await submitPublicEvaluationById(id, payload);
      }
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.message || 'Erro ao submeter respostas');
    }
  };

  if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!evaluation) return null;

  return (
    <Container maxWidth="md" sx={{ py: 6, minHeight: '100vh', display: 'flex', alignItems: 'flex-start' }}>
      <Paper sx={{ p: 4, width: '100%' }} elevation={2}>
        <Typography variant="h5" gutterBottom>{evaluation.title}</Typography>
        {evaluation.description && (
          <Typography variant="body1" color="text.secondary" gutterBottom sx={{ lineHeight: 1.6 }}>{evaluation.description}</Typography>
        )}

        {!submitted ? (
          <Box>
            {(() => {
              const descMap = {
                'Dados Demográficos': 'Ajude-nos a compreender o perfil dos participantes.',
                'Dados da Empresa': 'Informação geral sobre a organização.',
                'Aprendizagem e Dinâmica do Grupo': 'Avalie como a ação promoveu a aprendizagem e a dinâmica entre os participantes.',
                'Qualidade do Programa': 'Avalie a utilidade, organização e duração do curso.',
                'Qualidade dos Meios de Apoio': 'Avalie os recursos físicos e materiais disponibilizados.',
                'Desempenho da Equipa Pedagógica': 'Avalie a equipa pedagógica e o apoio prestado.',
                'Satisfação Global da Ação': 'Avalie a satisfação geral com a ação.',
                'Relevância': 'Avalie a relevância dos conteúdos e da experiência de aprendizagem.',
                'Alinhamento Estratégico': 'Avalie a ligação da formação aos objetivos estratégicos.',
                'Adaptação e Inovação': 'Avalie a capacidade de adaptação e inovação fomentada.',
                'Produtividade e Qualidade': 'Avalie impactos na produtividade e qualidade.',
                'Impacto Financeiro': 'Avalie redução de custos e ROI.',
                'Gestão de Pessoas': 'Avalie motivação, retenção e colaboração.',
                'Competitividade': 'Avalie impactos na competitividade e oportunidades de negócio.',
                'Indicadores Concretos': 'Registe impactos mensuráveis e observações.',
                'Opiniões e sugestões (Respostas livres)': 'Partilhe sugestões e observações.'
              };
              const qs = Array.isArray(evaluation.questions) ? evaluation.questions : [];
              const groups = {};
              qs.forEach((q, qi) => {
                const t = String(q.text || '');
                const g = t.includes('—') ? t.split('—')[0].trim() : t;
                if (!groups[g]) groups[g] = [];
                groups[g].push({ q, qi });
              });
              const order = [
                'Dados Demográficos',
                'Dados da Empresa',
                'Aprendizagem e Dinâmica do Grupo',
                'Qualidade do Programa',
                'Qualidade dos Meios de Apoio',
                'Desempenho da Equipa Pedagógica',
                'Satisfação Global da Ação',
                'Relevância',
                'Alinhamento Estratégico',
                'Adaptação e Inovação',
                'Produtividade e Qualidade',
                'Impacto Financeiro',
                'Gestão de Pessoas',
                'Competitividade',
                'Indicadores Concretos',
                'Opiniões e sugestões (Respostas livres)'
              ];
              return order.filter((g) => groups[g]).map((grp, gi) => (
                <Box key={`grp-${gi}`} sx={{ my: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ lineHeight: 1.5 }}>{grp}</Typography>
                  {descMap[grp] && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{descMap[grp]}</Typography>}
                  {groups[grp].map((item, idx) => {
                    const q = item.q;
                    const qid = item.qi;
                    const label = String(q.text || '').includes('—') ? String(q.text).split('—')[1].trim() : String(q.text || `Pergunta ${qid+1}`);
                    return (
                      <Box key={`q-${qid}`} sx={{ my: 2.5 }}>
                        <Typography variant="body2" gutterBottom sx={{ lineHeight: 1.5 }}>{label}{q.required ? ' *' : ''}</Typography>
                        {(q.type === 'rating') && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">1 Insuficiente · 2 Suficiente · 3 Médio · 4 Bom · 5 Excelente · Não se aplica</Typography>
                            <Rating value={typeof answers[qid] === 'number' ? answers[qid] : 0} onChange={(e, v) => handleChange(qid, Number(v || 0))} sx={{ mt: 0.5 }} />
                            <FormControlLabel control={<Radio checked={answers[qid] === 'na'} onChange={(e) => handleChange(qid, e.target.checked ? 'na' : 0)} />} label="Não Responde/Não se aplica" />
                            {validationErrors[qid] && (
                              <FormHelperText error>{validationErrors[qid]}</FormHelperText>
                            )}
                          </Box>
                        )}
                        {(q.type === 'text') && (
                          <Box>
                            <TextField fullWidth multiline rows={3} value={answers[qid] || ''} onChange={(e) => handleChange(qid, e.target.value)} />
                            {validationErrors[qid] && (
                              <FormHelperText error>{validationErrors[qid]}</FormHelperText>
                            )}
                          </Box>
                        )}
                        {(q.type === 'yesno') && (
                          <Box>
                            <RadioGroup value={answers[qid] || ''} onChange={(e) => handleChange(qid, e.target.value)}>
                              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
                              <FormControlLabel value="no" control={<Radio />} label="Não" />
                            </RadioGroup>
                            {validationErrors[qid] && (
                              <FormHelperText error>{validationErrors[qid]}</FormHelperText>
                            )}
                          </Box>
                        )}
                        {(q.type === 'choice') && Array.isArray(q.options) && (
                          <Box>
                            <FormControl component="fieldset">
                              <RadioGroup value={answers[qid] || ''} onChange={(e) => handleChange(qid, e.target.value)}>
                                {q.options.map((opt) => (
                                  <FormControlLabel key={`${qid}-${opt}`} value={opt} control={<Radio />} label={opt} />
                                ))}
                              </RadioGroup>
                            </FormControl>
                            {validationErrors[qid] && (
                              <FormHelperText error>{validationErrors[qid]}</FormHelperText>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ));
            })()}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleSubmit}>Submeter</Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>Obrigado por completar a avaliação!</Typography>
            <Typography>As suas respostas foram registadas com sucesso.</Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PublicEvaluation;
